import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase } from "@/lib/cms/cms-new-phrases";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Cross1Icon } from "@radix-ui/react-icons";
import { useQueryClient } from "@tanstack/react-query";
import { usePuck, type ComponentData } from "@measured/puck";
import AddCroppedImageForm from "@/components/images/AddCroppedImageForm";
import { cn } from "@/lib/utils";
import { BoElementImagePickerBody } from "@/components/cms-puck/fields/BoElementImagePicker";
import {
  closeMediaPicker,
  openMediaPicker,
  setMediaPickerTab,
  useMediaPickerState,
} from "@/components/cms-puck/fields/media-picker-store";
import { openImageStudio } from "@/components/cms-puck/fields/image-studio-store";
import { canRetouchAttachment } from "@/components/images/can-retouch-attachment";
import { usePuckMediaLocale } from "@/context/cms-puck/PuckEditorLocaleContext";
import { TRANSLATABLE_MEDIA_FIELDS } from "@/api/cms-puck/content-i18n";
import {
  attachmentThumb,
  attachmentTitle,
  cmsMediaLibraryWhere,
  type CatalogMediaKind,
} from "@/lib/cms-puck/media-utils";
import { AttachmentPixelSize } from "@/components/cms-puck/fields/AttachmentPixelSize";
import { Button } from "@/components/ui/button";
import SearchBar from "@/components/ui/searchbar";
import { useAuth } from "@/context/auth/AuthContext";
import { stripExtension } from "@/lib/utils";
import {
  useGetAllAttachments,
  useGetAttachmentById,
  type Attachment,
} from "@/api/attachment";
import { normalizeBlockStyle, type BlockStyle } from "@/lib/cms-puck/block-style";
import { Aperture } from "lucide-react";

/**
 * `fieldName` sentinelle : au lieu d'écrire une prop plate `props[fieldName]`, le
 * host écrit l'attachmentId dans le STYLE du bloc, à
 * `props.style.design.backgroundImage.attachmentId` (image de fond de section).
 */
export const STYLE_BG_TARGET = "@style.backgroundImage";

/**
 * Picker de média UNIFIÉ pour les blocs Puck (Media + Hero).
 *
 * Deux onglets dans un panneau portal (sans Radix Sheet, cf. PuckDrawerHost) :
 *  - « Bibliothèque » : médiathèque partagée (`docId = CMS.id` uploads Puck +
 *    `docId = Instance.id` covers de l'ancien CMS). 1 vignette par Attachment.
 *    Au clic, l'`id` est sélectionné.
 *  - « Ajouter un média » (Upload) : réutilise TEL QUEL le formulaire de l'ancien
 *    CMS (`AddCroppedImageForm`, le « Unified CMS media form » : image+resize /
 *    vidéo / embed) en ciblant `docId = CMS.id` + `forceCreateNewAttachment`. Au
 *    succès : refetch de la bibliothèque + sélection du nouveau média.
 *
 * L'id reste STRUCTUREL (partagé toutes langues, cf. config.tsx). La TAILLE
 * d'affichage (`size`) du bloc Media est un champ Puck `select` SÉPARÉ (un champ
 * custom Puck ne peut pas écrire une prop sœur de façon fiable). Légende/alt
 * viennent de l'Attachment. Le champ est aussi réutilisé tel quel par Hero
 * (couverture), qui n'a pas de taille.
 */

/** Tailles d'affichage du bloc Media (mapping URL identique BO ↔ front). */
export type MediaSize = "petit" | "medium" | "large" | "original";

/** Position dédiée aux médias importés depuis le CMS Puck. */
const CMS_PUCK_MEDIA_POSITION = "cmsPuckMedia";

/** Meilleure URL de vignette d'un attachment formaté (poster pour les vidéos). */
function thumbUrl(att: Attachment | undefined | null): string | undefined {
  return attachmentThumb(att);
}

function MediaThumb({
  att,
  size = 48,
  className,
  fill = false,
}: {
  att: Attachment | undefined | null;
  size?: number;
  className?: string;
  /** Remplit le conteneur parent (hauteur/largeur à 100 %). */
  fill?: boolean;
}) {
  const url = thumbUrl(att);
  // `shrink-0` + minWidth : dans la sidebar Puck (flex + overflow-x-hidden),
  // sans ça la vignette se fait écraser à 0px par les boutons (Changer/…).
  const boxStyle = fill
    ? undefined
    : ({ width: size, height: size, minWidth: size } as const);
  if (!url) {
    return (
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded bg-slate-100 text-[10px] text-slate-400",
          fill && "h-full w-full min-w-0",
          className,
        )}
        style={boxStyle}
      >
        {att?.kind === "embed" ? "embed" : att?.kind === "video" ? "vidéo" : "—"}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center overflow-hidden rounded bg-slate-100",
        fill && "h-full w-full min-w-0",
        className,
      )}
      style={boxStyle}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={url}
        alt={att?.name ?? ""}
        className="h-full w-full object-cover"
      />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Onglet Bibliothèque                                                        */
/* -------------------------------------------------------------------------- */

function LibraryTab({
  cmsId,
  instanceId,
  onPick,
}: {
  cmsId: string | undefined;
  instanceId: string | undefined;
  onPick: (id: string) => void;
}) {
  const cmsNew = useCmsNew();
  const [search, setSearch] = useState("");
  // Médiathèque partagée : CMS.id (uploads Puck) + Instance.id (covers ancien CMS).
  const { data: attachments = [], isLoading } = useGetAllAttachments({
    where: cmsMediaLibraryWhere(cmsId, instanceId),
    order: { order: "asc" },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return attachments;
    return attachments.filter((att) =>
      attachmentTitle(att).toLowerCase().includes(q),
    );
  }, [attachments, search]);

  if (isLoading) {
    return <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>;
  }

  return (
    <div className="space-y-3">
      <SearchBar
        search={search}
        onChangeSearch={setSearch}
        placeholder={cmsNew("cms.new.auto.placeholder.rechercher_dans_la_bibliotheque", "Rechercher dans la bibliothèque…")}
      />
      {attachments.length === 0 ? (
        <p className="text-sm text-slate-500">{cmsPhrase(cmsNew, "mediaLibraryEmpty")}</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.aucun_resultat", "Aucun résultat.")}</p>
      ) : (
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
          {filtered.map((att) => (
            <button
              key={att.id}
              type="button"
              onClick={() => onPick(att.id)}
              className="group flex flex-col items-center gap-1 rounded border border-transparent p-1 hover:border-slate-300"
              title={attachmentTitle(att)}
            >
              <MediaThumb att={att} size={96} />
              <span className="line-clamp-1 w-full text-center text-[11px] text-slate-600">
                {attachmentTitle(att)}
              </span>
              <AttachmentPixelSize att={att} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Onglet « Ajouter un média » — RÉUTILISE le formulaire de l'ancien CMS.      */
/* `AddCroppedImageForm` = le « Unified CMS media form » (image + resize /      */
/* vidéo / embed, champs multilingues) : on ne réimplémente RIEN, on l'embarque */
/* tel quel en ciblant `docId = CMS.id` + position dédiée, et en forçant la     */
/* création d'un NOUVEAU média (bibliothèque = on ajoute, on ne remplace pas).  */
/* -------------------------------------------------------------------------- */

function UploadTab({
  cmsId,
  onUploaded,
}: {
  cmsId: string | undefined;
  onUploaded: (id: string) => void;
}) {
  const cmsNew = useCmsNew();
  const queryClient = useQueryClient();

  if (!cmsId) {
    return (
      <p className="text-sm text-slate-500">{cmsPhrase(cmsNew, "mediasCmsNotFound")}</p>
    );
  }

  // Rafraîchit la bibliothèque (même queryKey que `useGetAllAttachments`).
  const refetch = () => {
    queryClient.invalidateQueries({ queryKey: ["artworks", "attachments"] });
  };

  return (
    <AddCroppedImageForm
      docId={cmsId}
      imagePosition={CMS_PUCK_MEDIA_POSITION}
      refetch={refetch}
      forceCreateNewAttachment
      onSuccess={(created) => {
        if (created.id) onUploaded(created.id);
      }}
      // Nom = fichier uploadé (pas un slug technique). Fallback si pas de fichier
      // (embed) : libellé générique court.
      buildImageName={(_instanceName, _docId, _position, _data, file) =>
        file ? stripExtension(file.name) : "embed"
      }
    />
  );
}

/* -------------------------------------------------------------------------- */
/* Drawer + champ                                                             */
/* -------------------------------------------------------------------------- */

function MediaPickerDrawer({
  open,
  onClose,
  onPick,
  allowedCatalogKinds,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (id: string) => void;
  allowedCatalogKinds: CatalogMediaKind[];
}) {
  const cmsNew = useCmsNew();
  const { instanceData } = useAuth();
  const cmsId = instanceData?.CMS?.id;
  const instanceId = instanceData?.id;
  const { tab } = useMediaPickerState();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="false"
      aria-label={cmsNew("cms.new.auto.label.choisir_un_media", "Choisir un média")}
      className={cn(
        // z-[110] + pointer-events-auto : au-dessus des Sheets Puck (z-100) et
        // cliquable même si un parent Radix modal a mis pointer-events:none sur body.
        "pointer-events-auto fixed inset-y-0 right-0 z-[110] flex w-full flex-col border-l bg-white shadow-xl",
        "duration-300 animate-in slide-in-from-right sm:max-w-2xl",
      )}
    >
      <div className="flex items-center gap-4 p-6 pb-4">
        <div className="grow text-lg font-semibold leading-normal text-[#0c111d] font-['Raleway']">
          {cmsPhrase(cmsNew, "chooseMedia")}
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          aria-label={cmsNew("cms.new.auto.label.fermer", "Fermer")}
        >
          <Cross1Icon className="h-4 w-4" />
        </button>
      </div>
      <div className="border-t" />
      <div className="flex flex-1 flex-col overflow-y-auto p-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {/* PAS de Radix Tabs : un Tabs démonte/remonte le contenu à chaque switch
            → le dialog se re-montait (flash). Ici les DEUX contenus restent montés
            en permanence et on bascule la VISIBILITÉ (CSS `hidden`). Le sélecteur
            est de simples boutons. Aucun mount/unmount au changement d'onglet. */}
        <div
          role="tablist"
          className="mb-4 inline-flex gap-1 rounded-lg bg-slate-100 p-1"
        >
          <TabButton
            active={tab === "library"}
            onClick={() => setMediaPickerTab("library")}
          >
            {cmsPhrase(cmsNew, "mediaTabLibrary")}
          </TabButton>
          <TabButton
            active={tab === "upload"}
            onClick={() => setMediaPickerTab("upload")}
          >
            {cmsPhrase(cmsNew, "addMedia")}
          </TabButton>
          <TabButton
            active={tab === "catalog"}
            onClick={() => setMediaPickerTab("catalog")}
          >
            {cmsPhrase(cmsNew, "mediaTabCatalog")}
          </TabButton>
        </div>

        <div className={tab === "library" ? "" : "hidden"}>
          <LibraryTab
            cmsId={cmsId}
            instanceId={instanceId}
            onPick={(id) => {
              onPick(id);
              onClose();
            }}
          />
        </div>
        <div className={tab === "upload" ? "" : "hidden"}>
          <UploadTab
            cmsId={cmsId}
            onUploaded={(id) => {
              onPick(id);
              setMediaPickerTab("library");
            }}
          />
        </div>
        <div className={tab === "catalog" ? "" : "hidden"}>
          <BoElementImagePickerBody
            allowedKinds={allowedCatalogKinds}
            onPick={(id) => {
              onPick(id);
              onClose();
            }}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Bouton de sélection d'onglet (segmented control) — pas de Radix. */
function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
        active
          ? "bg-white text-slate-900 shadow-sm"
          : "text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Champ Puck RÉUTILISABLE (Media + Hero) : sélectionne un `Attachment` et stocke
 * son `id` (STRUCTUREL). La TAILLE d'affichage (`size`) du bloc Media est un champ
 * Puck `select` distinct (cf. config.tsx).
 *
 * Le CHAMP ne fait qu'AFFICHER l'aperçu + OUVRIR le picker (via le store). Le
 * drawer lui-même vit dans `MediaPickerHost` (monté hors du panneau de champs) :
 * sinon le montage du formulaire d'upload désélectionne le bloc → Puck démonte
 * le champ → le drawer disparaissait. `fieldName` = la clé de prop à écrire.
 */
export function MediaPickerField({
  value,
  onChange,
  fieldName,
  label,
  allowedCatalogKinds,
}: {
  value: string | undefined;
  onChange: (value: string) => void;
  fieldName: string;
  label?: string;
  /** Kinds autorisés dans l'onglet Catalogue (défaut : image + vidéo + embed). */
  allowedCatalogKinds?: CatalogMediaKind[];
}) {
  const cmsNew = useCmsNew();
  // `label=""` : masque le titre interne (FieldsPanel affiche déjà `field.label`).
  // `label` omis : libellé par défaut « Média ».
  const resolvedLabel =
    label === ""
      ? ""
      : (label ?? cmsNew("cms.new.pickers.media.label", "Média"));
  // Aperçu de la sélection courante (fetch par id ; activé seulement si défini).
  const { data: current } = useGetAttachmentById(value ?? "");
  const selected = useMemo(
    () => (value ? (current as Attachment | undefined) : undefined),
    [value, current],
  );

  // Images par langue (cf. PER_LANGUAGE_IMAGES.md) : sur une langue NON-défaut,
  // un champ média traduisible affiche s'il HÉRITE de la base (langue par défaut)
  // ou porte une SURCHARGE propre à la langue, avec un bouton pour rétablir
  // l'héritage.
  const mediaLocale = usePuckMediaLocale();
  const { selectedItem } = usePuck();
  const blockType = selectedItem?.type as string | undefined;
  const blockId = (selectedItem?.props as { id?: string } | undefined)?.id;
  const isTranslatableMedia =
    !!blockType && (TRANSLATABLE_MEDIA_FIELDS[blockType] ?? []).includes(fieldName);
  const perLang =
    isTranslatableMedia &&
    !!mediaLocale &&
    mediaLocale.locale !== mediaLocale.defaultLocale;
  const baseValue =
    perLang && blockId
      ? mediaLocale!.mediaBase.get(blockId)?.[fieldName] ?? ""
      : "";
  const hasOverride = perLang && !!value && value !== baseValue;
  const localeName = (code?: string) =>
    cmsNew(`cms.new.locales.${code}`, (code ?? "").toUpperCase());

  return (
    <div className="space-y-2 p-1">
      {resolvedLabel ? (
        <div className="text-xs font-medium text-slate-600">{resolvedLabel}</div>
      ) : null}
      {perLang ? (
        <div
          className={`inline-flex w-fit items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium ${
            hasOverride
              ? "bg-blue-50 text-blue-700"
              : "bg-slate-100 text-slate-500"
          }`}
        >
          {hasOverride
            ? cmsNew("cms.new.pickers.media.langOverride", "Image propre à cette langue :") +
              ` ${localeName(mediaLocale!.locale)}`
            : cmsNew("cms.new.pickers.media.langInherits", "Hérite de la langue par défaut :") +
              ` ${localeName(mediaLocale!.defaultLocale)}`}
        </div>
      ) : null}
      <div className="flex items-stretch gap-3">
        <div className="relative w-[104px] shrink-0 self-stretch min-h-[104px]">
          <MediaThumb att={selected} fill />
          {selected && canRetouchAttachment(selected) && (
            <Button
              type="button"
              variant="white"
              className="absolute bottom-1 right-1 text-gray-950 text-sm flex gap-2"
              onClick={() => openImageStudio(selected)}
              title={cmsNew("cms.new.pickers.media.retouch", "Retoucher")}
              aria-label={cmsNew("cms.new.pickers.media.retouch", "Retoucher")}
            >
              <Aperture className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="flex flex-col justify-center gap-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              openMediaPicker(fieldName, { allowedCatalogKinds })
            }
          >
            {value ? cmsPhrase(cmsNew, "changeMedia") : cmsPhrase(cmsNew, "chooseMedia")}
          </Button>
          {perLang ? (
            hasOverride ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange(baseValue)}
              >
                {cmsNew("cms.new.pickers.media.resetToBase", "Rétablir l'héritage")}
              </Button>
            ) : null
          ) : (
            value && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange("")}
              >
                {cmsNew("cms.new.auto.title.retirer", "Retirer")}
              </Button>
            )
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * MediaPickerHost — monté UNE fois hors du panneau de champs (via
 * `overrides.puck` dans editor.tsx). Porte le drawer du picker, à l'abri des
 * démontages du champ. À l'ouverture, capture l'id du bloc sélectionné ; à la
 * sélection d'un média, écrit `props[fieldName] = mediaId` sur CE bloc (par son
 * id, via `getSelectorForId`), même si le bloc a été désélectionné entre-temps.
 */
export function MediaPickerHost() {
  const { selectedItem, dispatch, getItemById, getSelectorForId } = usePuck();
  const { open, fieldName, allowedCatalogKinds } = useMediaPickerState();
  const targetItemIdRef = useRef<string | null>(null);

  // À l'ouverture, mémorise le bloc ciblé (encore sélectionné à cet instant).
  useEffect(() => {
    if (open) {
      targetItemIdRef.current =
        (selectedItem?.props?.id as string | undefined) ?? null;
    }
    // On ne dépend QUE de `open` : capter au moment de l'ouverture.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handlePick = (mediaId: string) => {
    const itemId = targetItemIdRef.current;
    if (itemId && fieldName) {
      const selector = getSelectorForId(itemId);
      const node = getItemById(itemId) as ComponentData | undefined;
      if (selector && node) {
        // Cible imbriquée (image de fond de section) : on écrit dans le STYLE du
        // bloc plutôt qu'une prop plate. On repart du style normalisé pour ne
        // jamais produire un BlockStyle partiel.
        // Vignette de rubrique SelectionSections :
        // `fieldName = __selCatThumb__:<categoryId>`.
        const SEL_CAT_THUMB = "__selCatThumb__:";
        let nextProps: Record<string, unknown>;
        if (fieldName === STYLE_BG_TARGET) {
          const s = normalizeBlockStyle(
            node.props.style as BlockStyle | undefined,
          );
          nextProps = {
            ...node.props,
            style: {
              ...s,
              design: {
                ...s.design,
                backgroundImage: {
                  ...s.design.backgroundImage,
                  attachmentId: mediaId,
                },
              },
            },
          };
        } else if (fieldName.startsWith(SEL_CAT_THUMB)) {
          const catId = fieldName.slice(SEL_CAT_THUMB.length);
          const categories = Array.isArray(node.props.categories)
            ? (node.props.categories as { id?: string }[]).map((cat) =>
                cat?.id === catId
                  ? { ...cat, thumbnailAttachmentId: mediaId }
                  : cat,
              )
            : node.props.categories;
          nextProps = { ...node.props, categories };
        } else if (fieldName.startsWith("__decSectionVignette__:")) {
          // Éditeur inline Découpages (sidebar) : n'écrit pas dans les props
          // Puck — notifie l'éditeur local via CustomEvent.
          const sectionId = fieldName.slice("__decSectionVignette__:".length);
          window.dispatchEvent(
            new CustomEvent("cms-puck-dec-section-vignette", {
              detail: { sectionId, mediaId },
            }),
          );
          closeMediaPicker();
          return;
        } else {
          nextProps = { ...node.props, [fieldName]: mediaId };
        }
        dispatch({
          type: "replace",
          destinationIndex: selector.index,
          destinationZone: selector.zone,
          // `nextProps` est un Record spread de `node.props` (toujours avec `id`).
          data: {
            ...node,
            props: nextProps as ComponentData["props"],
          },
        });
      }
    }
    closeMediaPicker();
  };

  return (
    <MediaPickerDrawer
      open={open}
      allowedCatalogKinds={allowedCatalogKinds}
      onClose={closeMediaPicker}
      onPick={handlePick}
    />
  );
}
